import { FiHeadphones } from 'react-icons/fi';

export const listeningStrategiesData = {
  courseId: 'listening-strategies',
  courseType: 'listening-course',
  title: 'Listening Strategies',
  icon: FiHeadphones,
  overview: {
    impact: 'Foundation for all IELTS listening success',
    focus: 'Core strategies for each section',
    uses: 'All 4 listening sections',
    duration: '25 minutes',
  },
  sections: [
    {
      title: 'Understanding the IELTS Listening Test',
      content: `<p>The IELTS Listening test has 4 sections with increasing difficulty:</p>`,
      table: {
        headers: ['Section', 'Context', 'Difficulty'],
        rows: [
          ['Section 1', 'Social conversation (2 speakers)', 'Easiest'],
          ['Section 2', 'Social monologue (1 speaker)', 'Moderate'],
          ['Section 3', 'Academic discussion (2-4 speakers)', 'Challenging'],
          ['Section 4', 'Academic lecture (1 speaker)', 'Most difficult'],
        ],
      },
    },
    {
      title: 'Pre-Listening Strategies',
      content: `<p>Before the audio plays, prepare yourself:</p>`,
      points: [
        'Read questions carefully during the preview time',
        'Underline key words in questions (names, numbers, locations)',
        'Predict what type of answer is needed (noun, verb, number, name)',
        'Note word limits for each answer',
        'Identify distractors—multiple options might be mentioned',
      ],
    },
    {
      title: 'During Listening Strategies',
      content: `<p>While listening, stay focused and flexible:</p>`,
      points: [
        'Listen for signposting words: "firstly", "however", "finally"',
        'Don\'t get stuck—if you miss an answer, move on immediately',
        'Write answers as you hear them, don\'t wait',
        'Be aware of paraphrasing—answers rarely use exact question words',
        'Watch for corrections: "No wait, I meant..."',
      ],
      warning: 'You hear the audio ONCE only! Stay focused throughout.',
    },
    {
      title: 'Post-Listening Strategies',
      content: `<p>Use the 10 minutes at the end wisely:</p>`,
      points: [
        'Check spelling carefully (especially names and places)',
        'Verify word counts—don\'t exceed limits',
        'Ensure answers are grammatically correct',
        'Double-check number formats and capitalization',
        'Transfer answers neatly to the answer sheet',
      ],
    },
  ],
  exercises: [
    {
      title: 'Identify Answer Types',
      type: 'select',
      instructions: 'What type of answer is needed for each question?',
      questions: [
        {
          text: 'The museum opens at ___ am.',
          options: ['Number/Time', 'Name', 'Place'],
          answer: 'Number/Time',
          explanation: '"am" indicates a time is needed.',
        },
        {
          text: 'Visitors should go to the ___ desk first.',
          options: ['Place/Location', 'Person', 'Number'],
          answer: 'Place/Location',
          explanation: 'A type of desk (information, reception, etc.) is expected.',
        },
        {
          text: 'The tour costs $___ per person.',
          options: ['Number', 'Name', 'Action'],
          answer: 'Number',
          explanation: '"$" clearly indicates a price/number is needed.',
        },
        {
          text: 'Dr. ___ will lead the discussion.',
          options: ['Name', 'Place', 'Number'],
          answer: 'Name',
          explanation: '"Dr." indicates a person\'s surname is needed.',
        },
        {
          text: 'The workshop is held on ___ afternoons.',
          options: ['Day of week', 'Number', 'Name'],
          answer: 'Day of week',
          explanation: '"afternoons" suggests a day like "Monday" or "Friday".',
        },
        {
          text: 'Students should bring their own ___.',
          options: ['Object/Item', 'Person', 'Time'],
          answer: 'Object/Item',
          explanation: 'The sentence structure suggests a noun (item to bring).',
        },
      ],
    },
    {
      title: 'Recognize Signposting Words',
      type: 'select',
      instructions: 'What does each signposting phrase indicate?',
      questions: [
        {
          text: 'When you hear "On the other hand..."',
          options: ['Contrast/Different view', 'Same idea', 'Conclusion'],
          answer: 'Contrast/Different view',
          explanation: 'This phrase signals an opposing viewpoint is coming.',
        },
        {
          text: 'When you hear "To sum up..."',
          options: ['Conclusion', 'New topic', 'Example'],
          answer: 'Conclusion',
          explanation: 'This signals the speaker is about to summarize.',
        },
        {
          text: 'When you hear "Actually, I meant..."',
          options: ['Correction', 'Agreement', 'Question'],
          answer: 'Correction',
          explanation: 'IMPORTANT: The speaker is changing/correcting their previous answer!',
        },
        {
          text: 'When you hear "For instance..."',
          options: ['Example', 'Contrast', 'Conclusion'],
          answer: 'Example',
          explanation: 'An example or specific case is about to be given.',
        },
        {
          text: 'When you hear "Moving on to..."',
          options: ['Topic change', 'Summary', 'Correction'],
          answer: 'Topic change',
          explanation: 'The speaker is transitioning to a new topic—new answers coming!',
        },
      ],
    },
    {
      title: 'Spot the Distractor',
      type: 'select',
      instructions: 'In IELTS Listening, distractors are wrong answers mentioned before the correct one. Identify the trap.',
      questions: [
        {
          text: 'Audio: "The meeting was originally at 2pm, but it\'s been changed to 3pm." What time is the meeting?',
          options: ['3pm', '2pm'],
          answer: '3pm',
          explanation: '2pm is the distractor (original time). The correct answer is 3pm (changed time).',
        },
        {
          text: 'Audio: "You might think the library is on the first floor, but it\'s actually on the second floor." Which floor is the library on?',
          options: ['Second floor', 'First floor'],
          answer: 'Second floor',
          explanation: 'First floor is the distractor. "Actually" signals the correction.',
        },
        {
          text: 'Audio: "I was going to take the bus, but I decided to walk instead." How does the speaker travel?',
          options: ['Walk', 'Bus'],
          answer: 'Walk',
          explanation: 'Bus is the distractor (original plan). "Instead" signals the actual choice.',
        },
      ],
    },
    {
      title: 'Word Limit Check',
      type: 'select',
      instructions: 'Check if each answer follows the word limit rule.',
      questions: [
        {
          text: 'Word limit: NO MORE THAN TWO WORDS. Answer: "reception desk"',
          options: ['Correct', 'Too many words'],
          answer: 'Correct',
          explanation: '"Reception desk" is exactly 2 words—this is acceptable.',
        },
        {
          text: 'Word limit: ONE WORD ONLY. Answer: "the library"',
          options: ['Too many words', 'Correct'],
          answer: 'Too many words',
          explanation: '"The library" is 2 words. Write just "library".',
        },
        {
          text: 'Word limit: NO MORE THAN THREE WORDS. Answer: "at nine thirty"',
          options: ['Correct', 'Too many words'],
          answer: 'Correct',
          explanation: '"At nine thirty" is 3 words—acceptable for this limit.',
        },
      ],
    },
    {
      title: 'Pre-Listening Practice',
      type: 'writing',
      instructions: 'Look at each question and predict what type of information you need to listen for.',
      questions: [
        {
          text: 'The train departs at ___ and arrives at ___.',
          example: 'I need to listen for TWO times. I should note the departure time first, then the arrival time.',
          tip: 'Predict the format: probably times like "9:30" or "ten fifteen".',
        },
        {
          text: 'Applicants must be aged between ___ and ___.',
          example: 'I need TWO numbers for an age range. Listen for phrases like "minimum age" or "up to".',
          tip: 'Be ready for numbers and age-related vocabulary.',
        },
      ],
    },
  ],
  tips: [
    'Practice predicting answer types before listening',
    'Learn common signposting words by heart',
    'Never leave an answer blank—guess if needed',
    'Practice listening to various English accents',
  ],
  dailyPractice: [
    'Listen to English podcasts and predict what comes next',
    'Practice writing quickly while listening',
    'Time yourself transferring answers (10 minutes)',
    'Do IELTS practice tests under exam conditions',
  ],
  backPath: '/exams/english/#listening',
  backLabel: 'Back to Listening',
  aiContext: 'IELTS Listening Strategies: Core approaches for all 4 sections including pre-listening preparation, active listening techniques, and post-listening review.',
};

// Difficulty-variant factory: returns content adapted to the given difficulty level.
// difficulty 1-2: simplified content with basic examples and easier exercises
// difficulty 3: base content (default)
// difficulty 4-5: advanced content with complex examples and harder exercises
export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...listeningStrategiesData,
      title: listeningStrategiesData.title + ' (Foundations)',
      overview: {
        ...listeningStrategiesData.overview,
        duration: '15-20 minutes',
      },
      // Simplified: show only first 2 sections and first exercise set
      sections: listeningStrategiesData.sections.slice(0, Math.max(2, Math.ceil(listeningStrategiesData.sections.length / 2))),
      exercises: listeningStrategiesData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...listeningStrategiesData,
      title: listeningStrategiesData.title + ' (Advanced)',
      overview: {
        ...listeningStrategiesData.overview,
        duration: '30-35 minutes',
      },
      // Advanced: all sections plus advanced tips context
      sections: listeningStrategiesData.sections,
      exercises: listeningStrategiesData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(listeningStrategiesData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS writing tasks.',
      ],
    };
  }
  return listeningStrategiesData;
}
